import {
  analyse as engineAnalyse,
  diagnostics as engineDiagnose,
  diagnosticsAgainstSchema as engineDiagnoseAgainstSchema,
  parse as engineParse,
} from '@crown/dsl-compiler';
import Decimal from 'decimal.js';
import type {
  ExpressionDiagnostic,
  ExpressionFunctionMap,
  ExprNode,
  MatchPattern,
  Program,
  Stmt,
} from './ast.js';
import { knownFunctionNames } from './known-functions.js';
import type { Token } from './lexer.js';
import { ExpressionLexError, lexExpression } from './lexer.js';

/**
 * An example document, as a caller supplies one.
 *
 * Two forms reach the parser and both are navigated the same way: a sample
 * record whose members hold example VALUES (`{ patient: { age: 74 } }`), and a
 * schema document whose members are field definitions carrying `kind`/`type`
 * and their own members under `properties`. What a path RESOLVES to differs
 * between the two; whether a path exists does not.
 */
export type ExampleValue =
  | string
  | number
  | boolean
  | null
  | Date
  | ExampleValue[]
  | { readonly [member: string]: ExampleValue | undefined };

/** The members of an example document, keyed by name. */
export type ExampleMembers = {
  readonly [member: string]: ExampleValue | undefined;
};

/**
 * The members `value` holds, or `null` when it holds none.
 *
 * This is the one place a caller's raw document is examined. Everything below
 * navigates the members this answers rather than re-testing what a value is,
 * and a field definition's `properties` are folded in here so that a path
 * through a schema document reads the same as a path through a sample record.
 */
function membersOf(value: ExampleValue | undefined): ExampleMembers | null {
  const own = objectOf(value);
  if (!own) return null;
  const properties = objectOf(own.properties);
  const kind = textOf(own.kind) ?? textOf(own.type);
  return kind === 'object' && properties ? properties : own;
}

/**
 * The members `value` holds in its own right, or `null` when it holds none.
 *
 * Unlike `membersOf` this does not fold a field definition's `properties` in,
 * so a caller that needs the definition itself - its `kind`, its title - reads
 * it here.
 */
export function objectOf(
  value: ExampleValue | undefined,
): ExampleMembers | null {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value) || value instanceof Date) return null;
  return value instanceof Object ? value : null;
}

/** `value` as text, when it holds text. */
export function textOf(value: ExampleValue | undefined): string | undefined {
  return typeOfExample(value) === 'string' ? String(value) : undefined;
}

/** The type an expression reading `value` gets. */
export function typeOfExample(value: ExampleValue | undefined): InferredType {
  if (value === null) return 'null';
  if (value === undefined) return 'unknown';
  if (Array.isArray(value)) return 'array';
  if (value instanceof Date) return 'date';
  if (value instanceof Object) return value.currency ? 'currency' : 'object';
  // Boxing answers which primitive this is without asking for its `typeof`,
  // which describes a representation rather than what the document holds.
  const boxed: object = Object(value);
  if (boxed instanceof Number) return 'number';
  if (boxed instanceof String) return 'string';
  if (boxed instanceof Boolean) return 'boolean';
  return 'unknown';
}

/**
 * Options for `parseExpressionDetailed`.
 *
 * All properties are optional; when omitted the parser performs a
 * schema-agnostic parse (no root schema, no type checking).
 */
export interface ParseExpressionOptions {
  /** An example of the root document, used to validate `root.*` property access. */
  documentSchema?: ExampleValue;
  /** Context variable definitions from schema `ctx` block - enables ctx.* path validation */
  contextSchema?: ExampleMembers;
  /** Treat access to unknown root properties as an error instead of a warning. */
  treatUnknownRootAsError?: boolean;
  /** How to handle unknown nested property access on typed objects. */
  treatUnknownPropertyAs?: 'warn' | 'error' | 'ignore';
  /** Treat type mismatches (e.g. comparing a number to a string literal) as errors. */
  treatTypeMismatchAsError?: boolean;
  /** Override the built-in function table for function-call validation. */
  allowedFunctions?: ExpressionFunctionMap;
}

type InferredType =
  | 'number'
  | 'string'
  | 'boolean'
  | 'null'
  | 'array'
  | 'object'
  | 'date'
  | 'currency'
  | 'unknown';

/**
 * What `parseExpressionDetailed` answers: the tree, and everything an editor
 * needs in order to say something about it.
 */
export interface DetailedParse {
  /** The parse tree, absent when nothing could be parsed at all. */
  ast?: ExprNode;
  /** Every error and warning the parse produced, with source positions. */
  diagnostics: ExpressionDiagnostic[];
  /** The full token stream, comments included, for display. */
  tokens: Token[];
  /** The type the whole expression evaluates to. */
  expressionType?: InferredType;
  /** Every type a branching expression can evaluate to. */
  expressionTypeDetail?: InferredType[];
}

/**
 * Parse a DSL expression string and return the AST together with IDE-friendly
 * diagnostics (errors and warnings) and inferred type information.
 *
 * Unlike `compileExpression`, this function **never throws**. All parse errors
 * are collected into the `diagnostics` array and associated with source
 * positions so that Monaco / VS Code can underline the problematic tokens.
 *
 * This is the entry point used by the schema language server, the Monaco
 * editor integration, and the VS Code Crown Schema extension.
 *
 * @param expr    The expression source string.
 * @param options Optional documentSchema and type-checking options.
 * @returns An object with:
 *   - `ast` - the parsed `ExprNode` (or `undefined` on fatal parse error)
 *   - `diagnostics` - array of `ExpressionDiagnostic` objects (may be empty)
 *   - `tokens` - the raw token stream (for syntax highlighting)
 *   - `expressionType` - the inferred return type tag (e.g. `'number'`)
 *   - `expressionTypeDetail` - per-branch type breakdown for union types
 *
 * @example
 * const result = parseExpressionDetailed('value > 0', { documentSchema: { score: 'number' } });
 * result.diagnostics; // [] - no issues
 * result.expressionType; // 'boolean'
 *
 * @example
 * const result = parseExpressionDetailed('unknownFn(value)');
 * result.diagnostics[0]?.message; // 'Unknown function: unknownFn'
 */
export function parseExpressionDetailed(
  expr: string,
  options: ParseExpressionOptions = {},
): DetailedParse {
  const diagnostics: ExpressionDiagnostic[] = [];
  // Kept apart from the rest so the engine can answer for them - see
  // `engineSyntaxErrors`.
  const syntaxErrors: ExpressionDiagnostic[] = [];
  // The caller's documents are described once, here, rather than re-examined
  // at every path the parser walks.
  const documentMembers = membersOf(options.documentSchema);
  const contextMembers = options.contextSchema ?? null;
  let tokens: Token[] = [];
  // A caller may narrow what is callable - the SQL translator does, to add the
  // names it can translate - and otherwise the engine's own list is the answer.
  const allowed = options.allowedFunctions;
  const isKnown = (name: string): boolean =>
    allowed ? name in allowed : knownFunctionNames().has(name);
  try {
    tokens = lexExpression(expr);
  } catch (e) {
    // NOTE: tokens is assigned below after the full token stream is obtained
    const msg = e instanceof Error ? e.message : String(e);

    if (e instanceof ExpressionLexError) {
      diagnostics.push({
        message: msg,
        severity: 'error',
        index: e.index,
        length: e.length,
      });
    } else {
      diagnostics.push({
        message: msg,
        severity: 'error',
        index: 0,
        length: expr.length,
      });
    }
    return {
      ast: undefined,
      diagnostics: [...(engineSyntaxErrors(expr) ?? diagnostics)],
      tokens: [],
    };
  }
  // Comment tokens remain in the full token stream for display; the parser skips them
  let i = 0;
  const skipComments = () => {
    while (tokens[i]?.type === 'comment') i++;
  };
  const peek = () => {
    skipComments();
    return tokens[i];
  };
  const consume = (type?: Token['type'], value?: string) => {
    skipComments();
    const t = tokens[i];
    if (!t) {
      syntaxErrors.push({
        message: 'Unexpected end of input',
        severity: 'error',
        index: expr.length,
        length: 0,
      });
      return undefined;
    }
    if (type && t.type !== type) {
      syntaxErrors.push({
        message: `Expected ${type} got ${t.type}`,
        severity: 'error',
        index: positionOf(t),
        length: t.value.length,
      });
      i++;
      return undefined;
    }
    if (value && t.value !== value) {
      syntaxErrors.push({
        message: `Expected '${value}' got '${t.value}'`,
        severity: 'error',
        index: positionOf(t),
        length: t.value.length,
      });
      i++;
      return undefined;
    }
    i++;
    return t;
  };
  const positionOf = (t: Token) => t.index;
  function syntax(msg: string) {
    const t = peek();
    syntaxErrors.push({
      message: msg,
      severity: 'error',
      index: t ? positionOf(t) : expr.length,
      length: t ? t.value.length : 0,
    });
  }

  const parse = (): ExprNode | undefined => parseSequence();

  const parseSequence = (): ExprNode | undefined => {
    const statements: ExprNode[] = [];

    const first = parseStatement();
    if (!first) return undefined;
    statements.push(first);

    while (peek()?.type === 'semi') {
      consume('semi', ';');

      if (!peek()) break;

      const stmt = parseStatement();
      if (stmt) statements.push(stmt);
    }

    if (statements.length === 1) return statements[0];

    return { kind: 'sequence', statements };
  };

  const parseStatement = (): ExprNode | undefined => {
    const t = peek();

    if (t && t.type === 'keyword' && t.value === 'let') {
      consume('keyword', 'let');

      const nameTok = consume('ident');
      if (!nameTok) {
        syntax('Expected variable name after let');
        return undefined;
      }

      if (documentMembers && nameTok.value in documentMembers) {
        diagnostics.push({
          message: `Cannot use field name '${nameTok.value}' as variable name (conflicts with root.${nameTok.value})`,
          severity: 'error',
          index: positionOf(nameTok),
          length: nameTok.value.length,
        });
      }

      if (
        nameTok.value === 'root' ||
        nameTok.value === 'value' ||
        nameTok.value === 'true' ||
        nameTok.value === 'false' ||
        nameTok.value === 'null' ||
        nameTok.value === 'debugger'
      ) {
        diagnostics.push({
          message: `Cannot use reserved keyword '${nameTok.value}' as variable name`,
          severity: 'error',
          index: positionOf(nameTok),
          length: nameTok.value.length,
        });
      }

      const eqTok = consume('equals', '=');
      if (!eqTok) {
        syntax('Expected = after variable name');
        return undefined;
      }

      const value = parsePipeline();
      if (!value) {
        syntax('Expected expression after =');
        return undefined;
      }

      return { kind: 'assignment', name: nameTok.value, value };
    }

    if (t && t.type === 'keyword' && t.value === 'match') {
      return parseMatch();
    }

    return parsePipeline();
  };

  const parseIf = (): ExprNode | undefined => {
    consume('keyword', 'if');
    const cond = parseTernary();
    if (!cond) {
      syntax('Expected condition after if');
      return undefined;
    }
    // Contextual 'then' - detected as ident with value 'then'
    const maybeThen = peek();
    if (maybeThen && maybeThen.type === 'ident' && maybeThen.value === 'then') {
      consume('ident', 'then');
    } else {
      diagnostics.push({
        message: "Expected 'then' after if condition",
        severity: 'warn',
        index: (peek() ?? maybeThen)?.index ?? expr.length,
        length: 1,
      });
    }
    const thenBranch = parseTernary();
    if (!thenBranch) {
      syntax('Expected expression after then');
      return cond;
    }
    const maybeElse = peek();
    if (
      maybeElse &&
      maybeElse.type === 'keyword' &&
      maybeElse.value === 'else'
    ) {
      consume('keyword', 'else');
      const nextAfterElse = peek();
      const elseBranch =
        nextAfterElse &&
        nextAfterElse.type === 'keyword' &&
        nextAfterElse.value === 'if'
          ? parseIf()
          : parseTernary();
      if (!elseBranch)
        return {
          kind: 'ternary',
          cond,
          // oxlint-disable-next-line unicorn/no-thenable -- `then` is the ternary AST node's branch name, shared with the Rust port
          then: thenBranch,
          else: { kind: 'literal', value: null },
        };
      // oxlint-disable-next-line unicorn/no-thenable -- `then` is the ternary AST node's branch name, shared with the Rust port
      return { kind: 'ternary', cond, then: thenBranch, else: elseBranch };
    }
    return {
      kind: 'ternary',
      cond,
      // oxlint-disable-next-line unicorn/no-thenable -- `then` is the ternary AST node's branch name, shared with the Rust port
      then: thenBranch,
      else: { kind: 'literal', value: null },
    };
  };

  const parseForeach = (): ExprNode | undefined => {
    consume('keyword', 'foreach');
    const bindingTok = consume('ident');
    if (!bindingTok) {
      syntax('Expected binding variable name after foreach');
      return undefined;
    }
    const inTok = consume('keyword', 'in');
    if (!inTok) {
      syntax("Expected 'in' after foreach binding variable");
      return undefined;
    }
    const source = parseTernary();
    if (!source) {
      syntax('Expected source expression after in');
      return undefined;
    }
    const colonTok = consume('op', ':');
    if (!colonTok) {
      syntax("Expected ':' after foreach source expression");
      return undefined;
    }
    const body = parseTernary();
    if (!body) {
      syntax('Expected body expression after :');
      return undefined;
    }
    return { kind: 'foreach', binding: bindingTok.value, source, body };
  };

  const parseMatch = (): ExprNode | undefined => {
    consume('keyword', 'match');
    const subject = parseTernary();
    if (!subject) return undefined;

    const openBrace = consume('brace', '{');
    if (!openBrace) return undefined;

    const cases: Array<{
      pattern: MatchPattern;
      guard?: ExprNode;
      body: ExprNode;
    }> = [];

    const parseAtomicPattern = (): MatchPattern | undefined => {
      const t = peek();
      if (!t || t.value === '}') return undefined;
      // Wildcard
      if (t.type === 'ident' && t.value === '_') {
        consume('ident', '_');
        return { kind: 'wildcard' };
      }
      // Binding: bare ident not followed by . or (
      if (
        t.type === 'ident' &&
        t.value !== 'true' &&
        t.value !== 'false' &&
        t.value !== 'null'
      ) {
        const next = tokens[i + 1];
        if (!next || (next.type !== 'dot' && next.value !== '(')) {
          consume('ident');
          return { kind: 'binding_pat', name: t.value };
        }
      }
      const patExpr = parseFactor();
      if (!patExpr) return undefined;
      const rt = peek();
      if (rt && rt.type === 'op' && (rt.value === '..=' || rt.value === '..')) {
        const inclusive = rt.value === '..=';
        consume('op', rt.value);
        const toExpr = parseFactor();
        if (!toExpr) return { kind: 'literal_pat', expr: patExpr };
        return { kind: 'range_pat', from: patExpr, to: toExpr, inclusive };
      }
      return { kind: 'literal_pat', expr: patExpr };
    };

    const parseOrPattern = (): MatchPattern | undefined => {
      const first = parseAtomicPattern();
      if (!first) return undefined;
      if (peek()?.type === 'op' && peek()?.value === '|') {
        const patterns: MatchPattern[] = [first];
        while (peek()?.type === 'op' && peek()?.value === '|') {
          consume('op', '|');
          const sub = parseAtomicPattern();
          if (sub) patterns.push(sub);
        }
        return { kind: 'or_pat', patterns };
      }
      return first;
    };

    while (true) {
      const t = peek();
      if (!t || t.value === '}') break;

      const pattern = parseOrPattern();
      if (!pattern) break;

      // Optional `if` guard - `if` is now a keyword token
      let guard: ExprNode | undefined;
      const maybe = peek();
      if (maybe && maybe.type === 'keyword' && maybe.value === 'if') {
        consume('keyword', 'if');
        guard = parseTernary();
      }

      const arrow = consume('arrow', '=>');
      if (!arrow) break;

      const body = parseTernary();
      if (!body) break;

      cases.push({ pattern, guard, body });

      const nextTok = peek();
      if (nextTok && nextTok.type === 'comma') {
        consume('comma', ',');
        if (peek()?.value === '}') break;
      } else {
        break;
      }
    }

    consume('brace', '}');
    return { kind: 'match', expr: subject, cases };
  };

  const parsePipeline = (): ExprNode | undefined => {
    const node = parseTernary();
    if (!node) return undefined;

    const stages: ExprNode[] = [node];
    while (true) {
      const t = peek();
      if (!t || t.type !== 'pipe') break;
      consume('pipe', '|>');
      const stage = parseTernary();
      if (stage) stages.push(stage);
    }

    if (stages.length === 1) return node;
    return { kind: 'pipeline', stages };
  };
  const parseTernary = (): ExprNode | undefined => {
    const cond = parseOr();
    const t = peek();
    if (t && t.value === '?') {
      consume('op', '?');
      const thenExpr = parseTernary();
      const colonTok = peek();
      if (!colonTok || colonTok.value !== ':') {
        syntax('Expected : in ternary');
        return cond;
      }
      consume('op', ':');
      const elseExpr = parseTernary();
      if (cond && thenExpr && elseExpr)
        // oxlint-disable-next-line unicorn/no-thenable -- `then` is the ternary AST node's branch name, shared with the Rust port
        return { kind: 'ternary', cond, then: thenExpr, else: elseExpr };
      return cond;
    }
    return cond;
  };
  /**
   * @helper isRelationalOp
   * Returns true when a token is one of the eight relational/equality comparison operators:
   * `>`, `>=`, `<`, `<=`, `==`, `!=`, `===`, `!==`.
   * Used by parseOr / parseAnd to detect the shorthand comparison pattern where a logical
   * operator is immediately followed by a comparison operator.
   */
  const isRelationalOp = (t: Token): boolean =>
    t.type === 'op' &&
    ['>', '>=', '<', '<=', '==', '!=', '===', '!=='].includes(t.value);

  /**
   * @helper getComparisonSubject
   * If `node` is a direct binary comparison (`>`, `>=`, `<`, `<=`, `==`, `!=`, `===`, `!==`),
   * returns its left-hand operand so it can be reused as the implied subject in the shorthand
   * comparison syntax (`a >= 5 OR <= 10`). Returns `undefined` for any other node kind.
   */
  const getComparisonSubject = (
    node: ExprNode | undefined,
  ): ExprNode | undefined => {
    if (!node) return undefined;
    if (
      node.kind === 'binary' &&
      ['>', '>=', '<', '<=', '==', '!=', '===', '!=='].includes(node.op)
    ) {
      return node.left;
    }
    return undefined;
  };

  /**
   * @parser or-operator - Logical OR using `||`, `??`, or the `or` keyword
   * @syntax left || right
   * @syntax left ?? right
   * @syntax left or right
   *
   * The `or` keyword is a natural-language alias for `||` and produces an identical AST node.
   * `??` (nullish coalescing) is also processed at this level for precedence consistency.
   *
   * **Shorthand comparison syntax** - when the logical operator is immediately followed by a
   * relational/equality operator, the left-hand side of the preceding comparison is automatically
   * reused as the implied subject of the new comparison.
   *
   * @example isAdmin || isOwner
   *   Standard OR
   *
   * @example isAdmin or isOwner
   *   Keyword form - `or` is normalised to `||` in the AST
   *
   * @example value ?? defaultValue
   *   Nullish coalescing
   *
   * @example value >= 5 or <= 10
   *   Shorthand - desugars to `value >= 5 || value <= 10`
   *
   * @example value >= 5 || <= 10
   *   Same shorthand with symbol operator
   *
   * @example value >= 0 ?? <= 100
   *   Shorthand with nullish coalescing operator
   *
   * @example x + 1 >= 5 or <= 10
   *   Shorthand where the subject is a complex expression (`x + 1`)
   *
   * @example score >= 0 or <= 50 or >= 90
   *   Chained shorthand - applies recursively each iteration
   *
   * @note Shorthand fires only when a comparison op immediately follows the logical op.
   * @note Normal usage like `a > 5 or b > 2` is unaffected; `b` is not a comparison op.
   */
  const parseOr = (): ExprNode | undefined => {
    let node = parseAnd();
    let lastShorthandSubject: ExprNode | undefined;
    while (true) {
      const t = peek();
      if (!t) break;
      const isOrOp = t.value === '||' || t.value === '??';
      const isOrKeyword = t.type === 'keyword' && t.value === 'or';
      if (!isOrOp && !isOrKeyword) break;

      // Consume the logical operator token
      const logicalOp: string = isOrKeyword ? '||' : t.value;
      if (isOrKeyword) consume('keyword', 'or');
      else consume('op');

      // Shorthand: `a >= 5 OR <= 10`  →  `a >= 5 OR a <= 10`
      // Also supports chaining: `a >= 1 OR <= 5 OR >= 8` reuses the same subject
      const next = peek();
      if (next && isRelationalOp(next)) {
        const subject = getComparisonSubject(node) ?? lastShorthandSubject;
        if (subject) {
          lastShorthandSubject = subject;
          const compTok = consume('op');
          if (!compTok) break;
          const compOp = compTok.value;
          const rhs = parseAdd();
          if (rhs && node) {
            const shorthand: ExprNode = {
              kind: 'binary',
              op: compOp,
              left: subject,
              right: rhs,
            };
            node = {
              kind: 'binary',
              op: logicalOp,
              left: node,
              right: shorthand,
            };
          }
          continue;
        }
      }

      // Normal case
      lastShorthandSubject = undefined;
      const rhsStart = i; // record position before parsing rhs
      const rhs = parseAnd();
      if (rhs && node) {
        // AND/OR diagnostic: warn when 'and' and 'or' are mixed without brackets
        // Only fire when the AND sub-expression is NOT explicitly wrapped in parentheses.
        // If the first token of the rhs was '(', the user explicitly grouped it.
        const wasParenthesized =
          tokens[rhsStart]?.type === 'paren' && tokens[rhsStart]?.value === '(';
        if (rhs.kind === 'binary' && rhs.op === '&&' && !wasParenthesized) {
          const interpretation = describeNode({
            kind: 'binary',
            op: logicalOp,
            left: node,
            right: rhs,
          });
          const diagToken = tokens[i - 1] ?? tokens[i];
          diagnostics.push({
            message: `'and' binds tighter than 'or' \u2014 currently evaluating as: ${interpretation}. Add parentheses to confirm this is what you mean.`,
            severity: 'warn',
            index: diagToken ? diagToken.index : 0,
            length: diagToken ? diagToken.length : 1,
          });
        }
        node = { kind: 'binary', op: logicalOp, left: node, right: rhs };
      }
    }
    return node;
  };

  /** Produce a human-readable representation of a binary node, parenthesising 'and' groups nested under 'or'. */
  const describeNode = (node: ExprNode): string => {
    if (node.kind !== 'binary') {
      if (node.kind === 'literal') return String(node.value);
      if (node.kind === 'identifier') return node.chain.join('.');
      return '(...)';
    }
    const opStr = node.op === '&&' ? 'and' : node.op === '||' ? 'or' : node.op;
    const leftStr = describeNode(node.left);
    const rightStr = describeNode(node.right);
    const wrapLeft =
      node.op === '||' && node.left.kind === 'binary' && node.left.op === '&&';
    const wrapRight =
      node.op === '||' &&
      node.right.kind === 'binary' &&
      node.right.op === '&&';
    const l = wrapLeft ? `(${leftStr})` : leftStr;
    const r = wrapRight ? `(${rightStr})` : rightStr;
    return `${l} ${opStr} ${r}`;
  };

  /**
   * @parser and-operator - Logical AND using `&&` or the `and` keyword
   * @syntax left && right
   * @syntax left and right
   *
   * The `and` keyword is a natural-language alias for `&&` and produces an identical AST node.
   * AND binds more tightly than OR, following standard logical precedence.
   *
   * **Shorthand comparison syntax** - when `&&` or `and` is immediately followed by a
   * relational/equality operator, the left-hand side of the preceding comparison is automatically
   * reused as the implied subject of the new comparison.
   *
   * @example age >= 18 && hasConsent
   *   Standard AND
   *
   * @example age >= 18 and hasConsent
   *   Keyword form - `and` is normalised to `&&` in the AST
   *
   * @example value >= 5 and <= 10
   *   Shorthand - desugars to `value >= 5 && value <= 10`
   *
   * @example value >= 5 && <= 10
   *   Same shorthand with symbol operator
   *
   * @example age >= 18 and <= 65
   *   Shorthand range check
   *
   * @example x + 1 >= 5 and <= 10
   *   Shorthand where the subject is a complex expression (`x + 1`)
   *
   * @example score >= 2 and <= 8 and != 5
   *   Chained shorthand - applies recursively; equivalent to `score>=2 && score<=8 && score!=5`
   *
   * @note Shorthand fires only when a comparison op immediately follows `&&` / `and`.
   * @note Normal usage like `a > 5 and b > 2` is unaffected; `b` is not a comparison op.
   */
  const parseAnd = (): ExprNode | undefined => {
    let node = parseEquality();
    let lastShorthandSubject: ExprNode | undefined;
    while (true) {
      const t = peek();
      if (!t) break;
      const isAndOp = t.value === '&&';
      const isAndKeyword = t.type === 'keyword' && t.value === 'and';
      if (!isAndOp && !isAndKeyword) break;

      // Consume the logical operator token
      if (isAndKeyword) consume('keyword', 'and');
      else consume('op', '&&');

      // Shorthand: `a >= 5 AND <= 10`  →  `a >= 5 AND a <= 10`
      // Also supports chaining: `a >= 2 AND <= 8 AND != 5` reuses the same subject
      const next = peek();
      if (next && isRelationalOp(next)) {
        const subject = getComparisonSubject(node) ?? lastShorthandSubject;
        if (subject) {
          lastShorthandSubject = subject;
          const compTok = consume('op');
          if (!compTok) break;
          const compOp = compTok.value;
          const rhs = parseAdd();
          if (rhs && node) {
            const shorthand: ExprNode = {
              kind: 'binary',
              op: compOp,
              left: subject,
              right: rhs,
            };
            node = { kind: 'binary', op: '&&', left: node, right: shorthand };
          }
          continue;
        }
      }

      // Normal case
      lastShorthandSubject = undefined;
      const rhs = parseEquality();
      if (rhs && node) {
        node = { kind: 'binary', op: '&&', left: node, right: rhs };
      }
    }
    return node;
  };
  const parseEquality = (): ExprNode | undefined => {
    let node = parseRel();
    while (true) {
      const t = peek();
      if (!t) break;
      if (['==', '!=', '===', '!=='].includes(t.value)) {
        const op = consume('op')?.value || '==';
        const rhs = parseRel();
        if (rhs && node) {
          node = { kind: 'binary', op, left: node, right: rhs };
        }
      } else if (t.type === 'keyword' && t.value === 'in') {
        consume('keyword', 'in');
        const rhs = parseRel();
        if (rhs && node) {
          node = { kind: 'binary', op: 'in', left: node, right: rhs };
        }
      } else if (
        t.type === 'keyword' &&
        t.value === 'not' &&
        tokens[i + 1]?.type === 'keyword' &&
        tokens[i + 1]?.value === 'in'
      ) {
        consume('keyword', 'not');
        consume('keyword', 'in');
        const rhs = parseRel();
        if (rhs && node) {
          node = { kind: 'binary', op: 'not in', left: node, right: rhs };
        }
      } else {
        break;
      }
    }
    return node;
  };
  const parseRel = (): ExprNode | undefined => {
    let node = parseAdd();
    while (true) {
      const t = peek();
      if (!t || !['>', '>=', '<', '<='].includes(t.value)) break;
      const op = consume('op')?.value || '>';
      const rhs = parseAdd();
      if (rhs && node) {
        node = { kind: 'binary', op, left: node, right: rhs };
      }
    }
    return node;
  };
  const parseAdd = (): ExprNode | undefined => {
    let node = parseTerm();
    while (true) {
      const t = peek();
      if (!t || (t.value !== '+' && t.value !== '-')) break;
      const op = consume('op')?.value || '+';
      const rhs = parseTerm();
      if (rhs && node) {
        node = { kind: 'binary', op, left: node, right: rhs };
      }
    }
    return node;
  };
  const parseTerm = (): ExprNode | undefined => {
    let node = parseExponent();
    while (true) {
      const t = peek();
      if (!t || (t.value !== '*' && t.value !== '/' && t.value !== '%')) break;
      const op = consume('op')?.value || '*';
      const rhs = parseExponent();
      if (rhs && node) {
        node = { kind: 'binary', op, left: node, right: rhs };
      }
    }
    return node;
  };
  const parseExponent = (): ExprNode | undefined => {
    const left = parseFactor();
    if (!left) return undefined;
    const t = peek();
    if (t && t.value === '**') {
      consume('op', '**');
      const right = parseExponent();
      if (right) {
        return { kind: 'binary', op: '**', left, right };
      }
    }
    return left;
  };
  const parseFactor = (): ExprNode | undefined => {
    const t = peek();

    if (t && t.type === 'type') {
      consume('type');
      const arg = parseFactor();
      return arg ? { kind: 'typeof', arg } : undefined;
    }

    if (t && t.type === 'op' && t.value === '!') {
      consume('op', '!');
      const arg = parseFactor();
      return arg ? { kind: 'unary', op: '!', arg } : undefined;
    }
    if (t && t.type === 'keyword' && t.value === 'not') {
      consume('keyword', 'not');
      const arg = parseFactor();
      return arg ? { kind: 'unary', op: '!', arg } : undefined;
    }
    if (t && t.type === 'op' && t.value === '-') {
      consume('op', '-');
      const arg = parseFactor();
      return arg ? { kind: 'unary', op: '-', arg } : undefined;
    }
    return parseMemberAccess();
  };

  const parseMemberAccess = (): ExprNode | undefined => {
    let base = parsePrimaryLiteral();
    if (!base) return undefined;

    while (true) {
      const nt = peek();
      if (!nt) break;

      // Bracket subscript access: split(value, '|')[0] or root.arr[0]
      if (nt.type === 'bracket' && nt.value === '[') {
        consume('bracket', '[');
        const indexExpr = parseTernary();
        consume('bracket', ']');
        if (indexExpr) {
          base = {
            kind: 'call',
            name: '__member_access__',
            args: [base, indexExpr, { kind: 'literal', value: false }],
          };
        }
        continue;
      }

      // Accept keyword tokens as property/method names (e.g. obj.if, obj.match, obj.foreach)
      const nextIsIdentOrKeyword =
        tokens[i + 1]?.type === 'ident' || tokens[i + 1]?.type === 'keyword';
      // `a.b(...)`, and the OPTIONAL form `a.b?.(...)` - `?.` guards invocation
      // the same way it guards access, and a shipped schema writes it.
      const callBracketAt =
        tokens[i + 2]?.value === '('
          ? 2
          : tokens[i + 2]?.value === '?.' && tokens[i + 3]?.value === '('
            ? 3
            : -1;
      if (
        (nt.value === '.' || nt.value === '?.') &&
        nextIsIdentOrKeyword &&
        callBracketAt !== -1
      ) {
        const isOptional = nt.value === '?.';
        if (isOptional) {
          consume('op', '?.');
        } else {
          consume('dot', '.');
        }
        const methodName =
          tokens[i]?.type === 'keyword' ? tokens[i++] : consume('ident');
        if (!methodName) break;

        if (peek()?.value === '?.') consume('op', '?.');
        consume('paren', '(');
        const args: ExprNode[] = [];
        const firstArgTok = peek();
        if (firstArgTok && firstArgTok.value !== ')') {
          while (true) {
            const arg = parseTernary();
            if (arg) args.push(arg);
            const argNext = peek();
            if (argNext && argNext.type === 'comma') {
              consume('comma', ',');
              continue;
            }
            break;
          }
        }
        consume('paren', ')');

        base = {
          kind: 'call',
          name: methodName.value,
          args: [base, ...args],
        };
        continue;
      }

      if (nt.value === '.' || nt.value === '?.') {
        const isOptional = nt.value === '?.';
        if (isOptional) {
          consume('op', '?.');
        } else {
          consume('dot', '.');
        }
        const propertyCandidate = tokens[i];
        const propName =
          propertyCandidate &&
          ['ident', 'keyword', 'type'].includes(propertyCandidate.type)
            ? tokens[i++]
            : consume('ident');
        if (!propName) break;

        base = {
          kind: 'call',
          name: propName.value,
          args: [base],
        };
        continue;
      }

      break;
    }

    return base;
  };

  const parsePrimaryLiteral = (): ExprNode | undefined => {
    const t = peek();
    if (!t) {
      syntax('Unexpected end');
      return undefined;
    }

    if (t.type === 'formula') {
      consume('formula');
      const inner = parseTernary();
      if (!inner) {
        syntax('Expected expression after @[');
        return undefined;
      }
      const closeBracket = consume('bracket', ']');
      if (!closeBracket) {
        syntax('Expected ] to close formula');
      }
      return inner;
    }

    if (t.type === 'number') {
      consume('number');
      return { kind: 'literal', value: Number(t.value.replaceAll('_', '')) };
    }
    if (t.type === 'string') {
      consume('string');
      return { kind: 'literal', value: t.value };
    }
    if (t.type === 'template') {
      consume('template');
      const raw = t.value;
      const parts: ExprNode[] = [];
      let tpos = 0;
      while (tpos < raw.length) {
        const open = raw.indexOf('{', tpos);
        if (open === -1) {
          const lit = raw.slice(tpos);
          if (lit) parts.push({ kind: 'literal', value: lit });
          break;
        }
        if (open > tpos)
          parts.push({ kind: 'literal', value: raw.slice(tpos, open) });
        let tdepth = 1;
        let tj = open + 1;
        while (tj < raw.length && tdepth > 0) {
          if (raw[tj] === '{') tdepth++;
          else if (raw[tj] === '}') tdepth--;
          tj++;
        }
        if (tdepth !== 0) {
          diagnostics.push({
            message: 'Unclosed template interpolation',
            severity: 'error',
            index: t.index + open + 1,
            length: 1,
          });
          return undefined;
        }
        const exprSrc = raw.slice(open + 1, tj - 1).trim();
        if (exprSrc) {
          const tr = parseExpressionDetailed(exprSrc);
          diagnostics.push(...tr.diagnostics);
          if (tr.ast) parts.push(tr.ast);
        } else {
          diagnostics.push({
            message: 'Empty template interpolation',
            severity: 'error',
            index: t.index + open + 1,
            length: 2,
          });
          return undefined;
        }
        tpos = tj;
      }
      const [firstPart, ...restParts] = parts;
      if (!firstPart) return { kind: 'literal', value: '' };
      let tnode: ExprNode = firstPart;
      for (const part of restParts) {
        tnode = { kind: 'binary', op: '+', left: tnode, right: part };
      }
      return tnode;
    }
    if (t.type === 'date') {
      consume('date');

      return { kind: 'literal', value: new Date(t.value) };
    }
    if (t.type === 'currency') {
      consume('currency');

      const value = new Decimal(t.value.replaceAll('_', ''));
      return { kind: 'literal', value: { value, currency: t.currency } };
    }
    if (t.type === 'keyword' && t.value === 'debugger') {
      consume('keyword', 'debugger');
      return { kind: 'debugger' };
    }

    if (t.type === 'keyword' && t.value === 'match') {
      return parseMatch();
    }
    if (t.type === 'keyword' && t.value === 'if') {
      return parseIf();
    }
    if (t.type === 'keyword' && t.value === 'foreach') {
      return parseForeach();
    }

    if (t.type === 'keyword' && t.value === 'true') {
      consume('keyword', 'true');
      return { kind: 'literal', value: true };
    }
    if (t.type === 'keyword' && t.value === 'false') {
      consume('keyword', 'false');
      return { kind: 'literal', value: false };
    }
    if (t.type === 'keyword' && t.value === 'null') {
      consume('keyword', 'null');
      return { kind: 'literal', value: null };
    }

    if (t.value === '[') {
      consume('bracket', '[');
      const elements: ExprNode[] = [];
      const firstElementTok = peek();
      if (firstElementTok && firstElementTok.value !== ']') {
        while (true) {
          const el = parseTernary();
          if (el) elements.push(el);
          const nt = peek();
          if (nt && nt.type === 'comma') {
            consume('comma', ',');

            if (peek()?.value === ']') break;
            continue;
          }
          break;
        }
      }
      consume('bracket', ']');
      return { kind: 'array', elements };
    }

    if (t.value === '{') {
      consume('brace', '{');
      const properties: Array<{ key: string; value: ExprNode }> = [];
      const firstPropertyTok = peek();
      if (firstPropertyTok && firstPropertyTok.value !== '}') {
        while (true) {
          const keyTok = consume('ident');
          if (!keyTok) break;
          consume('op', ':');
          const value = parseTernary();
          if (value) properties.push({ key: keyTok.value, value });
          const nt = peek();
          if (nt && nt.type === 'comma') {
            consume('comma', ',');

            if (peek()?.value === '}') break;
            continue;
          }
          break;
        }
      }
      consume('brace', '}');
      return { kind: 'object', properties };
    }

    if (t.type === 'ident') {
      const nameTok = consume('ident');
      if (!nameTok) return undefined;

      if (peek()?.value === '(') {
        consume('paren', '(');
        const args: ExprNode[] = [];
        const firstArgTok = peek();
        if (firstArgTok && firstArgTok.value !== ')') {
          while (true) {
            const arg = parseTernary();
            if (arg) args.push(arg);
            const nt = peek();
            if (nt && nt.type === 'comma') {
              consume('comma', ',');
              continue;
            }
            break;
          }
        }
        consume('paren', ')');
        if (!isKnown(nameTok.value)) {
          diagnostics.push({
            message: `Unknown function '${nameTok.value}'`,
            severity: 'error',
            index: positionOf(nameTok),
            length: nameTok.value.length,
          });
        }
        return { kind: 'call', name: nameTok.value, args };
      }

      const chain = [nameTok.value];
      const optional: boolean[] = [];
      while (true) {
        const nt = peek();
        if (!nt) break;
        /*
          Stop before a METHOD name.

          This loop eats a dotted path greedily, and it used to eat a segment
          that is actually a method call: `value.toLowerCase()` became the path
          `value.toLowerCase` with `()` left over, reported as "Trailing
          input", and `root.a.trim()` became the path `root.a.trim` and was
          reported as "Unknown property 'trim'". Both messages point away from
          the cause.

          The postfix loop above already knows how to parse a method call - it
          just never got the chance, because the path was consumed first. So
          when the token after the property is `(`, the chain ends here and
          leaves the call to the code that handles calls.

          `compiler.ts` never had this problem: it builds the chain one segment
          at a time and checks for `(` at each step, which is why the runtime
          evaluated `value?.toLowerCase()` happily while the importer called it
          a syntax error and refused the document.
        */
        if (
          (nt.value === '.' || nt.value === '?.') &&
          (tokens[i + 2]?.value === '(' ||
            (tokens[i + 2]?.value === '?.' && tokens[i + 3]?.value === '('))
        ) {
          break;
        }
        if (nt.value === '?.') {
          consume('op', '?.');
          optional.push(true);
          const propertyCandidate = tokens[i];
          const nx =
            propertyCandidate &&
            ['ident', 'keyword', 'type'].includes(propertyCandidate.type)
              ? tokens[i++]
              : consume('ident');
          if (nx) chain.push(nx.value);
        } else if (nt.value === '.') {
          consume('dot', '.');
          optional.push(false);
          const propertyCandidate = tokens[i];
          const nx =
            propertyCandidate &&
            ['ident', 'keyword', 'type'].includes(propertyCandidate.type)
              ? tokens[i++]
              : consume('ident');
          if (nx) chain.push(nx.value);
        } else {
          break;
        }
      }

      if (documentMembers && chain[0] === 'root') {
        let members: ExampleMembers | null = documentMembers;
        const offsetIndex = positionOf(nameTok);
        for (let ci = 1; ci < chain.length; ci++) {
          const seg = chain[ci] ?? '';
          const tokForSeg = tokens.find(
            (tt) =>
              ['ident', 'keyword', 'type'].includes(tt.type) &&
              tt.value === seg &&
              tt.index >= offsetIndex,
          );
          if (members && seg in members) {
            members = membersOf(members[seg]);
          } else {
            const sev =
              options.treatUnknownPropertyAs === 'ignore'
                ? undefined
                : options.treatUnknownPropertyAs || 'warn';
            if (sev)
              diagnostics.push({
                message: `Unknown property '${seg}' in path root.${chain.slice(1, ci + 1).join('.')}`,
                severity: sev,
                index: tokForSeg ? tokForSeg.index : positionOf(nameTok),
                length: seg.length,
              });
            break;
          }
        }
      }

      // Validate ctx.* path against contextSchema
      if (contextMembers && chain[0] === 'ctx') {
        let members: ExampleMembers | null = contextMembers;
        const offsetIndex = positionOf(nameTok);
        for (let ci = 1; ci < chain.length; ci++) {
          const seg = chain[ci] ?? '';
          const tokForSeg = tokens.find(
            (tt) =>
              tt.type === 'ident' &&
              tt.value === seg &&
              tt.index >= offsetIndex,
          );
          if (members && seg in members) {
            members = membersOf(members[seg]);
          } else {
            const sev =
              options.treatUnknownPropertyAs === 'ignore'
                ? undefined
                : options.treatUnknownPropertyAs || 'warn';
            if (sev)
              diagnostics.push({
                message: `Unknown context variable '${seg}' in path ctx.${chain.slice(1, ci + 1).join('.')}`,
                severity: sev,
                index: tokForSeg ? tokForSeg.index : positionOf(nameTok),
                length: seg.length,
              });
            break;
          }
        }
      }

      return {
        kind: 'identifier',
        chain,
        optional: optional.length > 0 ? optional : undefined,
      };
    }

    /**
     * @parser arrow-function - Arrow function (lambda) with optional parameters and block bodies
     * @syntax (param1, param2?, ...) => expression
     * @syntax (param1, param2?, ...) => { statements }
     *
     * @example (x) => x * 2
     *   Single parameter, expression body, implicit return
     *
     * @example (a, b) => a + b
     *   Multiple parameters, expression body
     *
     * @example (a, b?) => a + (b ?? 0)
     *   Optional parameter (b), uses nullish coalescing for default
     *
     * @example (str) => upper(str)
     *   Calls DSL function
     *
     * @example (a, b) => { a + b }
     *   Block body with single expression, returns the expression value
     *
     * @example (a, b, c) => { let total = a + b + c; total }
     *   Block body with multiple statements, returns last expression (total)
     *
     * @example () => 42
     *   Zero parameters
     *
     * @note Optional parameters (marked with ?) default to undefined if not provided
     * @note Block bodies return the value of the last expression (implicit return)
     * @note Expressions are evaluated in lexical scope with parameter bindings
     * @note Used extensively for IMask prepare/commit/dispatch functions
     */

    if (t.value === '(') {
      const _parenStart = positionOf(t);
      consume('paren', '(');

      const savedPos = i;
      const params: Array<{ name: string; optional: boolean }> = [];
      let isArrowFunction = false;

      const firstParamTok = peek();
      if (firstParamTok?.type === 'ident') {
        const firstParam = firstParamTok.value;
        i++;
        const isOptional = peek()?.value === '?';
        if (isOptional) i++;
        params.push({ name: firstParam, optional: isOptional });

        while (peek()?.type === 'comma') {
          i++;
          const paramTok = peek();
          if (paramTok?.type === 'ident') {
            const paramName = paramTok.value;
            i++;
            const paramOptional = peek()?.value === '?';
            if (paramOptional) i++;
            params.push({ name: paramName, optional: paramOptional });
          }
        }

        if (peek()?.value === ')') {
          i++;
          if (peek()?.type === 'arrow') {
            isArrowFunction = true;
          }
        }
      } else if (peek()?.value === ')') {
        i++;
        if (peek()?.type === 'arrow') {
          isArrowFunction = true;
        }
      }

      i = savedPos;

      if (isArrowFunction) {
        const arrowParams: Array<{ name: string; optional: boolean }> = [];
        if (peek()?.type === 'ident') {
          while (peek()?.type === 'ident') {
            const paramTok = consume('ident');
            if (paramTok) {
              const isOptional = peek()?.value === '?';
              if (isOptional) consume('op', '?');
              arrowParams.push({ name: paramTok.value, optional: isOptional });
            }
            if (peek()?.type === 'comma') {
              consume('comma', ',');
            }
          }
        }

        consume('paren', ')');
        consume('arrow', '=>');

        let body: ExprNode;
        if (peek()?.value === '{') {
          consume('brace', '{');
          const statements: ExprNode[] = [];

          for (
            let stmtTok = peek();
            stmtTok && stmtTok.value !== '}';
            stmtTok = peek()
          ) {
            const stmt = parseStatement();
            if (stmt) statements.push(stmt);

            if (peek()?.type === 'semi') {
              consume('semi', ';');
            }
          }

          consume('brace', '}');

          const [onlyStatement] = statements;
          body =
            statements.length === 1 && onlyStatement
              ? onlyStatement
              : { kind: 'sequence', statements };
        } else {
          const exprBody = parseTernary();
          if (!exprBody) {
            syntax('Expected arrow function body');
            return undefined;
          }
          body = exprBody;
        }

        return { kind: 'arrow', params: arrowParams, body };
      } else {
        const inner = parse();
        consume('paren', ')');
        return inner;
      }
    }

    syntax(`Unexpected token ${t.value}`);
    consume();
    return undefined;
  };
  const ast = parse();
  if (peek()) syntax('Trailing input');

  const inferIdentifierType = (chain: string[]): InferredType => {
    if (!chain.length) return 'unknown';
    const rootName = chain[0];
    if (rootName === 'value') return 'unknown';
    if (rootName === 'root') {
      let members: ExampleMembers | null = documentMembers;
      if (!members) return 'unknown';
      let member: ExampleValue | undefined = options.documentSchema;
      for (let index = 1; index < chain.length; index++) {
        const seg = chain[index] ?? '';
        if (!members || !(seg in members)) return 'unknown';
        member = members[seg];
        members = membersOf(member);
      }
      return typeOfExample(member);
    }
    return 'unknown';
  };
  const infer = (node?: ExprNode): InferredType => {
    if (!node) return 'unknown';
    switch (node.kind) {
      case 'sequence':
        if (node.statements.length === 0) return 'unknown';
        return infer(node.statements[node.statements.length - 1]);
      case 'assignment':
        return infer(node.value);
      case 'debugger':
        return 'unknown';
      case 'literal':
        // SAFETY: literal nodes are built in this file out of lexer tokens, so
        // the value is a string, number, boolean or null. The AST types it
        // loosely because that union is shared with the engine's own tree.
        return typeOfExample(node.value as ExampleValue);
      case 'identifier':
        return inferIdentifierType(node.chain);
      case 'call': {
        const mathNumFuncs = [
          'len',
          'abs',
          'min',
          'max',
          'sum',
          'average',
          'mean',
          'median',
          'mode',
          'variance',
          'stdev',
          'count',
          'countif',
          'trunc',
          'sign',
          'exp',
          'log',
          'log10',
          'sin',
          'cos',
          'tan',
          'asin',
          'acos',
          'atan',
          'atan2',
          'radians',
          'degrees',
          'pi',
          'e',
          'random',
          'randomInt',
          'clamp',
          'lerp',
          'percentage',
          'percentile',
          'year',
          'month',
          'day',
          'hour',
          'minute',
          'second',
          'hours',
          'minutes',
          'seconds',
          'weekday',
          'daysInMonth',
          'quarterOfYear',
          'weekOfYear',
          'age',
          'businessDays',
        ];
        if (mathNumFuncs.includes(node.name)) return 'number';

        const dateFuncs = [
          'addDays',
          'addMonths',
          'addYears',
          'addHours',
          'addMinutes',
          'startOfDay',
          'endOfDay',
          'startOfMonth',
          'endOfMonth',
          'parseDate',
          'date',
        ];
        if (dateFuncs.includes(node.name)) return 'date';

        const stringFuncs = ['weekdayName', 'monthName', 'formatDate'];
        if (stringFuncs.includes(node.name)) return 'string';

        const boolFuncs = [
          'isLeapYear',
          'isBetween',
          'isBefore',
          'isAfter',
          'isSameDay',
        ];
        if (boolFuncs.includes(node.name)) return 'boolean';

        return 'unknown';
      }
      case 'typeof': {
        return 'string';
      }
      case 'unary': {
        if (node.op === '!') return 'boolean';
        const t = infer(node.arg);
        return t === 'number' ? 'number' : 'unknown';
      }
      case 'binary': {
        const lt = infer(node.left);
        const rt = infer(node.right);
        switch (node.op) {
          case '+': {
            if (lt === 'string' || rt === 'string') return 'string';
            if (lt === 'number' && rt === 'number') return 'number';
            if (lt !== 'unknown' && rt !== 'unknown')
              typeMismatch(node, lt, rt, ['number', 'string']);
            return 'unknown';
          }
          case '-':
          case '*':
          case '/':
          case '**': {
            if (lt === 'number' && rt === 'number') return 'number';
            if (lt !== 'unknown' && rt !== 'unknown')
              typeMismatch(node, lt, rt, ['number']);
            return 'unknown';
          }
          case '>':
          case '>=':
          case '<':
          case '<=': {
            if (
              lt === rt &&
              (lt === 'number' || lt === 'string' || lt === 'date')
            )
              return 'boolean';
            if (lt !== 'unknown' && rt !== 'unknown')
              typeMismatch(node, lt, rt, ['number', 'string', 'date']);
            return 'boolean';
          }
          case '==':
          case '!=':
            return 'boolean';
          case '&&':
          case '||':
            return 'unknown';
          case '??': {
            if (lt !== 'unknown' && lt !== 'null') return lt;
            return rt;
          }
          default:
            return 'unknown';
        }
      }
      case 'ternary': {
        const tt = infer(node.then);
        const et = infer(node.else);
        if (tt === et) return tt;
        return 'unknown';
      }
      case 'array':
        return 'array';
      case 'object':
        return 'object';
      case 'pipeline': {
        if (node.stages.length === 0) return 'unknown';
        return infer(node.stages[node.stages.length - 1]);
      }
      case 'match': {
        const [firstCase, ...restCases] = node.cases;
        if (!firstCase) return 'unknown';
        const firstType = infer(firstCase.body);
        for (const armCase of restCases) {
          if (infer(armCase.body) !== firstType) return 'unknown';
        }
        return firstType;
      }
    }
    return 'unknown';
  };
  function typeMismatch(
    node: ExprNode,
    lt: InferredType,
    rt: InferredType,
    expected: InferredType[],
  ) {
    const sev: 'error' | 'warn' = options.treatTypeMismatchAsError
      ? 'error'
      : 'warn';

    const pos = findNodeStart(node) ?? 0;
    diagnostics.push({
      message: `Type mismatch for operator '${'op' in node ? node.op : node.kind}': left=${lt} right=${rt} expected ${expected.join(' or ')}`,
      severity: sev,
      index: pos,
      length: 1,
    });
  }
  function findNodeStart(node: ExprNode): number | undefined {
    switch (node.kind) {
      case 'debugger': {
        const tok = tokens.find(
          (t) => t.type === 'ident' && t.value === 'debugger',
        );
        return tok?.index;
      }
      case 'literal': {
        const tok = tokens.find((t) => t.value == String(node.value));
        return tok?.index;
      }
      case 'identifier': {
        const tok = tokens.find(
          (t) => t.type === 'ident' && t.value === node.chain[0],
        );
        return tok?.index;
      }
      case 'unary':
        return findNodeStart(node.arg);
      case 'typeof':
        return findNodeStart(node.arg);
      case 'binary':
        return findNodeStart(node.left);
      case 'ternary':
        return findNodeStart(node.cond);
    }
  }
  // Everything but the tokens from the engine, whenever it can answer.
  const fromEngine = engineAnalysis(expr, options, isKnown);
  if (fromEngine) {
    return {
      ast: engineTree(expr) ?? ast,
      diagnostics: [
        ...(engineSyntaxErrors(expr) ?? syntaxErrors),
        ...fromEngine.diagnostics,
      ],
      tokens,
      expressionType: fromEngine.type,
      expressionTypeDetail: fromEngine.detail,
    };
  }

  const expressionType = infer(ast);

  const detailSet = new Set<InferredType>();
  function collect(node?: ExprNode) {
    if (!node) return;
    switch (node.kind) {
      case 'debugger':
        return;
      case 'binary': {
        if (['||', '??', '&&'].includes(node.op)) {
          const lt = infer(node.left);
          const rt = infer(node.right);
          if (lt) detailSet.add(lt);
          if (rt) detailSet.add(rt);
        }
        collect(node.left);
        collect(node.right);
        break;
      }
      case 'ternary': {
        const tt = infer(node.then);
        const et = infer(node.else);
        if (tt) detailSet.add(tt);
        if (et) detailSet.add(et);
        collect(node.then);
        collect(node.else);
        break;
      }
      case 'unary':
        collect(node.arg);
        break;
      case 'typeof':
        collect(node.arg);
        break;
      case 'call':
        for (const a of node.args) collect(a);
        break;
    }
  }
  collect(ast);
  const expressionTypeDetail = detailSet.size
    ? Array.from(detailSet.values())
    : undefined;
  return {
    ast: engineTree(expr) ?? ast,
    // Syntax first: a caller showing one diagnostic shows the one that stopped
    // the parse, not a warning about what it managed to read anyway.
    diagnostics: [
      ...(engineSyntaxErrors(expr) ?? syntaxErrors),
      ...diagnostics,
    ],
    tokens,
    expressionType,
    expressionTypeDetail,
  };
}

/**
 * The parse tree, from the engine that evaluates the expression.
 *
 * WHY THE TREE COMES FROM THERE. The parser in this file also builds one, and
 * on 202 shared cases the two agreed 195 times. The seven disagreements were
 * all this one being wrong:
 *
 *   `value.first()`            the CALL disappeared, leaving the chain
 *                              `["value", "first"]` - as if a property had been
 *                              read
 *   `value?.toLowerCase()`     the same, through an optional chain
 *   `map(items, x => x + b)`   the lambda's BODY was dropped, so `b` was
 *                              invisible to anything walking the tree
 *   `foreach x in xs { … }`    not parsed at all
 *
 * Things walk this tree: the SQL translator decides what it can emit, the
 * duplicate detector reads which fields a rule compares, the reporting and
 * analytics engines read calculated fields. A tree that loses a call or a
 * lambda body is one they read a different expression from than the one that
 * runs.
 *
 * The tokens and the inferred type above still come from this file - the editor
 * needs both, and the engine reports neither - so what is replaced is the part
 * where the two disagreed.
 *
 * `undefined` when the engine cannot parse it, which leaves the local tree in
 * place: a caller asking for diagnostics on a broken expression should still
 * get whatever could be read of it.
 */
function engineTree(expr: string): ExprNode | undefined {
  try {
    // SAFETY: the engine's tree and this file's `ExprNode` are the same shape -
    // 195 of 202 cases byte-identical, and the seven that differed are the ones
    // above, where the engine has MORE of the expression rather than a
    // different notion of it.
    return (engineParse(expr).ast as ExprNode | null) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * What the engine says of an expression beyond its syntax: the type it comes
 * to, every type a branch can come to, unknown functions, paths the caller's
 * documents do not declare, and warnings - `and` mixed with `or` without
 * brackets, operands an operator cannot take, a `let` that shadows a field.
 *
 * WHY THE ENGINE'S. This file typed calls from a list of names kept by hand,
 * checked only `root.` paths - never a bare field name - and reported
 * `let root = ...` as an error, which the language allows and a host's
 * prelude relies on. The engine types a call from its builtin's declared
 * signature and checks paths the way it runs them.
 *
 * `null` when the engine cannot be reached - a browser before `init()` - and
 * this file's own findings stand in.
 */
function engineAnalysis(
  expr: string,
  options: ParseExpressionOptions,
  isKnown: (name: string) => boolean,
): {
  type: InferredType;
  detail?: InferredType[];
  diagnostics: ExpressionDiagnostic[];
} | null {
  try {
    const declared = {
      root: options.documentSchema,
      ctx: options.contextSchema,
    };
    const analysis = engineAnalyse(expr, declared);
    const diagnostics: ExpressionDiagnostic[] = [];
    // An unknown function, unless the caller supplies it: the SQL translator
    // names the functions it can translate beyond the builtins.
    for (const d of engineDiagnose(expr)) {
      if (d.code !== 'unknown-function') continue;
      const name = /'([^']+)'/.exec(d.message)?.[1];
      if (name && isKnown(name)) continue;
      diagnostics.push({
        message: d.message.charAt(0).toUpperCase() + d.message.slice(1),
        severity: 'error',
        index: d.index,
        length: d.length,
      });
    }
    const pathSeverity = options.treatUnknownPropertyAs ?? 'warn';
    if (
      pathSeverity !== 'ignore' &&
      (options.documentSchema !== undefined ||
        options.contextSchema !== undefined)
    ) {
      for (const d of engineDiagnoseAgainstSchema(expr, declared)) {
        if (d.code !== 'unknown-property') continue;
        const rootPath = d.message.includes(' in path root.');
        diagnostics.push({
          message: d.message,
          severity:
            rootPath && options.treatUnknownRootAsError
              ? 'error'
              : pathSeverity,
          index: d.index,
          length: d.length,
        });
      }
    }
    for (const w of analysis.warnings) {
      diagnostics.push({
        message: w.message,
        severity:
          w.code === 'type-mismatch' && options.treatTypeMismatchAsError
            ? 'error'
            : 'warn',
        index: w.index,
        length: w.length,
      });
    }
    // SAFETY: the engine answers one of the nine type names InferredType
    // lists; it is the same set, named the same way.
    const type = analysis.type as InferredType;
    const detail = analysis.detail.length
      ? (analysis.detail as InferredType[])
      : undefined;
    return { type, detail, diagnostics };
  } catch {
    return null;
  }
}

/**
 * The syntax errors to report: the ENGINE's, whenever it can speak.
 *
 * WHY THE ENGINE'S RATHER THAN THIS FILE'S. The parser here exists for what the
 * engine does not do - inferred types, unknown properties against a document,
 * a variable named after a field - and it has to parse the source to get there.
 * Its parse is not the language's, though. On the shared corpus it rejected two
 * expressions the engine accepts: `map(items, x => x + offset)`, whose
 * single-parameter lambda it read as a parenthesised expression, and
 * `foreach row in rows { row + bonus }`, which it wanted a colon in. Both are
 * valid rules, and an editor underlined them in red.
 *
 * So syntax is answered by the thing that decides it: lexing, parsing, and the
 * literals a compile pass rejects, such as `2024-02-30`. What this file still
 * owns is everything the engine has no opinion about - inferred types, unknown
 * properties, a variable named after a field - which is merged around these.
 *
 * Answers `null` when the engine cannot be reached at all - during module
 * start-up in a browser, before `init()` has resolved - in which case the local
 * errors stand rather than nothing being reported.
 */
function engineSyntaxErrors(expr: string): ExpressionDiagnostic[] | null {
  try {
    return engineDiagnose(expr)
      .filter(
        (issue) =>
          issue.severity === 'error' &&
          (issue.code === 'lex' ||
            issue.code === 'parse' ||
            issue.code === 'compile'),
      )
      .map((issue) => ({
        // A diagnostic already says it is an error, and the stage it came from
        // is not something the reader of a tooltip can act on. The engine's
        // compile-stage errors carry their variant as a prefix and its
        // parse-stage ones do not, so this is what makes the two read alike.
        message: issue.message.replace(/^(?:Parse|Lex|Compile) error: /u, ''),
        severity: 'error' as const,
        index: issue.index,
        length: issue.length,
      }));
  } catch {
    return null;
  }
}

/**
 * Parse an expression that has already been lexed.
 *
 * DEPRECATED IN EFFECT: the tokens are ignored and the source is re-parsed from
 * scratch by {@link parseExpressionDetailed}. It remains so existing callers
 * compile. Call `parseExpressionDetailed` directly.
 */
export function parseExpressionFromTokens(
  _tokens: Token[],
  expr: string,
  options: ParseExpressionOptions = {},
) {
  return parseExpressionDetailed(expr, options);
}

// ─── Program-level parser ────────────────────────────────────────────────────

/** Find the index of the closing '}' that matches the '{' at `bracePos` in `str`. */
function findMatchingBrace(str: string, bracePos: number): number {
  let depth = 0;
  let inStr = false;
  let strChar = '';
  for (let i = bracePos; i < str.length; i++) {
    const c = str[i] ?? '';
    if (inStr) {
      if (c === '\\') {
        i++;
        continue;
      }
      if (c === strChar) inStr = false;
      continue;
    }
    if (c === '"' || c === "'") {
      inStr = true;
      strChar = c;
      continue;
    }
    if (c === '`') {
      i++;
      while (i < str.length && str[i] !== '`') i++;
      continue;
    }
    if (c === '{') depth++;
    if (c === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return str.length - 1;
}

/** Split a program source string into top-level statement strings. */
function splitTopLevelStatements(src: string): string[] {
  const stmts: string[] = [];
  let start = 0;
  let depth = 0;
  let inStr = false;
  let strChar = '';
  let i = 0;
  const flush = (end: number) => {
    const s = src.slice(start, end).trim();
    if (s) stmts.push(s);
    start = end + 1;
  };
  while (i < src.length) {
    const c = src[i] ?? '';
    if (inStr) {
      if (c === '\\') {
        i += 2;
        continue;
      }
      if (c === strChar) inStr = false;
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      inStr = true;
      strChar = c;
      i++;
      continue;
    }
    if (c === '`') {
      i++;
      while (i < src.length && src[i] !== '`') i++;
      i++;
      continue;
    }
    if (c === '{') {
      depth++;
      i++;
      continue;
    }
    if (c === '}') {
      depth--;
      i++;
      if (depth === 0) {
        let j = i;
        while (
          j < src.length &&
          (src[j] === ' ' || src[j] === '\t' || src[j] === '\r')
        )
          j++;
        if (src.startsWith('otherwise', j) || src.startsWith('else', j))
          continue;
        flush(i);
        continue;
      }
      continue;
    }
    if (depth === 0 && (c === '\n' || c === ';')) {
      flush(i);
      i++;
      continue;
    }
    i++;
  }
  const last = src.slice(start).trim();
  if (last) stmts.push(last);
  return stmts;
}

/** One parsed statement, with whatever parsing it had to report. */
interface ParsedStatement {
  /** The statement, which is an expression statement when nothing else fits. */
  node: Stmt;
  /** Every error and warning this statement produced. */
  diags: ExpressionDiagnostic[];
}

function parseSingleStmt(stmtSrc: string): ParsedStatement {
  const diags: ExpressionDiagnostic[] = [];
  const s = stmtSrc.trim();

  if (/^use\s+/.test(s)) {
    const mod = s.slice(4).trim();
    const KNOWN_MODULES = [
      'system',
      'files',
      'path',
      'process',
      'csv',
      'http',
      'std',
    ] as const;
    const known = KNOWN_MODULES.find((name) => name === mod);
    if (!known) {
      diags.push({
        message: `Unknown module '${mod}': available modules are ${KNOWN_MODULES.join(', ')}`,
        severity: 'error',
        index: 4,
        length: mod.length,
      });
    }
    return { node: { kind: 'stmt_use', module: known ?? 'std' }, diags };
  }

  if (/^return(\s|$)/.test(s)) {
    const rest = s.slice(6).trim();
    if (!rest) return { node: { kind: 'stmt_return' }, diags };
    const r = parseExpressionDetailed(rest);
    diags.push(...r.diagnostics);
    return { node: { kind: 'stmt_return', value: r.ast }, diags };
  }

  if (/^when[\s{]/.test(s)) return parseWhenBlock(s, diags);

  if (/^let\s+/.test(s)) {
    const r = parseExpressionDetailed(s);
    diags.push(...r.diagnostics);
    if (r.ast?.kind === 'assignment') {
      return {
        node: { kind: 'stmt_let', name: r.ast.name, value: r.ast.value },
        diags,
      };
    }
    return {
      node: {
        kind: 'stmt_expr',
        node: r.ast ?? { kind: 'literal', value: null },
      },
      diags,
    };
  }

  const r = parseExpressionDetailed(s);
  diags.push(...r.diagnostics);
  return {
    node: {
      kind: 'stmt_expr',
      node: r.ast ?? { kind: 'literal', value: null },
    },
    diags,
  };
}

function parseWhenBlock(
  s: string,
  diags: ExpressionDiagnostic[],
): ParsedStatement {
  const afterKw = s.slice('when'.length);
  let condEnd = -1;
  let d = 0;
  let inS = false;
  let sC = '';
  for (let i = 0; i < afterKw.length; i++) {
    const c = afterKw[i] ?? '';
    if (inS) {
      if (c === '\\') {
        i++;
      } else if (c === sC) inS = false;
      continue;
    }
    if (c === '"' || c === "'") {
      inS = true;
      sC = c;
      continue;
    }
    if (c === '{' && d === 0) {
      condEnd = i;
      break;
    }
    if (c === '{') d++;
    if (c === '}') d--;
  }
  if (condEnd === -1) {
    diags.push({
      message: "Expected '{' after when condition",
      severity: 'error',
      index: 0,
      length: s.length,
    });
    return {
      node: { kind: 'stmt_expr', node: { kind: 'literal', value: null } },
      diags,
    };
  }
  const condSrc = afterKw.slice(0, condEnd).trim();
  const condR = parseExpressionDetailed(condSrc);
  diags.push(...condR.diagnostics);
  const cond: ExprNode = condR.ast ?? { kind: 'literal', value: false };
  const rest = afterKw.slice(condEnd);
  const thenClose = findMatchingBrace(rest, 0);
  const thenBody = rest.slice(1, thenClose).trim();
  const afterThen = rest.slice(thenClose + 1).trim();
  const thenR = parseProgram(thenBody);
  diags.push(...thenR.diagnostics);
  let elseStmts: Stmt[] = [];
  if (/^(otherwise|else)\b/.test(afterThen)) {
    const skip = afterThen.startsWith('otherwise') ? 9 : 4;
    const afterOW = afterThen.slice(skip).trim();
    if (afterOW.startsWith('{')) {
      const elseClose = findMatchingBrace(afterOW, 0);
      const elseR = parseProgram(afterOW.slice(1, elseClose).trim());
      diags.push(...elseR.diagnostics);
      elseStmts = elseR.program.stmts;
    }
  }
  return {
    node: {
      kind: 'stmt_when',
      cond,
      thenBody: thenR.program.stmts,
      elseBody: elseStmts,
    },
    diags,
  };
}

/** A parsed program, with everything parsing it reported. */
export interface ParsedProgram {
  /** The program's statements. */
  program: Program;
  /** Every error and warning across all of them. */
  diagnostics: ExpressionDiagnostic[];
}

/**
 * Parse a DSL script (multi-statement program) into a `Program` AST.
 *
 * Supports: `use system/files`, `print expr`, `when cond { } otherwise { }`,
 * `let name = expr`, `return expr`, and expression statements.
 * Template literals (`\`Hello {name}\``) are desugared to `+` chains.
 *
 * @param src  The full program source string.
 * @returns `{ program, diagnostics }` - never throws.
 */
export function parseProgram(src: string): ParsedProgram {
  const allDiags: ExpressionDiagnostic[] = [];
  const stmts: Stmt[] = [];
  for (const stmtSrc of splitTopLevelStatements(src)) {
    const { node, diags } = parseSingleStmt(stmtSrc);
    allDiags.push(...diags);
    stmts.push(node);
  }
  return { program: { kind: 'program', stmts }, diagnostics: allDiags };
}
